import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { 
  Users, Eye, Clock, Monitor, Smartphone, Tablet, 
  TrendingUp, Activity, RefreshCw, Shield, LogIn,
  Globe, Share2
} from 'lucide-react';

const ADMIN_EMAIL = 'celian@nthg.fr';

const AdminPage = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  
  const [stats, setStats] = useState({
    total: 0,
    unique: 0,
    today: 0,
    live: 0,
    todayUnique: 0
  });
  const [recentVisits, setRecentVisits] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [devices, setDevices] = useState({ mobile: 0, desktop: 0, tablet: 0 });
  const [browsers, setBrowsers] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [countries, setCountries] = useState([]);
  const [sources, setSources] = useState([]);

  // Vérifier si admin connecté
  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAdmin(user?.email === ADMIN_EMAIL);
      setIsLoading(false);
    };
    checkAdmin();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAdmin(session?.user?.email === ADMIN_EMAIL);
    });

    return () => authListener?.subscription?.unsubscribe();
  }, []);

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
    }
    setLoginLoading(false);
  };

  // Charger les stats
  const loadStats = useCallback(async () => {
    if (!isAdmin) return;

    try {
      // Exclure les pages admin des stats
      const { data: allVisits } = await supabase
        .from('visits')
        .select('visitor_id, page, device_type, browser, created_at, country, source, medium')
        .neq('page', 'admin');

      if (allVisits) {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const fiveMinAgo = new Date(now - 5 * 60 * 1000);

        const uniqueVisitors = new Set(allVisits.map(v => v.visitor_id)).size;
        const todayVisits = allVisits.filter(v => new Date(v.created_at) >= todayStart);
        const liveVisits = allVisits.filter(v => new Date(v.created_at) >= fiveMinAgo);

        setStats({
          total: allVisits.length,
          unique: uniqueVisitors,
          today: todayVisits.length,
          todayUnique: new Set(todayVisits.map(v => v.visitor_id)).size,
          live: new Set(liveVisits.map(v => v.visitor_id)).size
        });

        // Top pages (exclure events)
        const pageCount = {};
        allVisits.forEach(v => {
          if (!v.page.startsWith('event:')) {
            pageCount[v.page] = (pageCount[v.page] || 0) + 1;
          }
        });
        setTopPages(
          Object.entries(pageCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([page, count]) => ({ page, count }))
        );

        // Devices
        const deviceCount = { mobile: 0, desktop: 0, tablet: 0 };
        allVisits.forEach(v => {
          if (v.device_type) deviceCount[v.device_type]++;
        });
        setDevices(deviceCount);

        // Browsers
        const browserCount = {};
        allVisits.forEach(v => {
          if (v.browser) browserCount[v.browser] = (browserCount[v.browser] || 0) + 1;
        });
        setBrowsers(
          Object.entries(browserCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([name, count]) => ({ name, count }))
        );

        // Pays
        const countryCount = {};
        allVisits.forEach(v => {
          const c = v.country || 'Inconnu';
          countryCount[c] = (countryCount[c] || 0) + 1;
        });
        setCountries(
          Object.entries(countryCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([name, count]) => ({ name, count }))
        );

        // Sources
        const sourceCount = {};
        allVisits.forEach(v => {
          const s = v.source || 'direct';
          sourceCount[s] = (sourceCount[s] || 0) + 1;
        });
        setSources(
          Object.entries(sourceCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([name, count]) => ({ name, count }))
        );

        // Daily stats (7 derniers jours)
        const dailyMap = {};
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(d.getDate() - i);
          const key = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
          dailyMap[key] = { views: 0, visitors: new Set() };
        }
        allVisits.forEach(v => {
          const d = new Date(v.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
          if (dailyMap[d]) {
            dailyMap[d].views++;
            dailyMap[d].visitors.add(v.visitor_id);
          }
        });
        setDailyStats(
          Object.entries(dailyMap).map(([date, data]) => ({
            date,
            views: data.views,
            visitors: data.visitors.size
          }))
        );
      }

      // Dernières visites (exclure admin)
      const { data: recent } = await supabase
        .from('visits')
        .select('*')
        .neq('page', 'admin')
        .order('created_at', { ascending: false })
        .limit(15);
      
      setRecentVisits(recent || []);

    } catch (err) {
      console.error('Stats error:', err);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      loadStats();
      const interval = setInterval(loadStats, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, loadStats]);

  // Page de login
  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Chargement...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-login-card">
          <Shield size={48} className="admin-login-icon" />
          <h1>Admin Dashboard</h1>
          <p>Connectez-vous pour accéder aux statistiques</p>
          
          <form onSubmit={handleLogin} className="admin-login-form">
            {error && <div className="admin-error">{error}</div>}
            <input
              type="email"
              placeholder="Email admin"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button type="submit" disabled={loginLoading}>
              <LogIn size={18} />
              {loginLoading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const getPageLabel = (page) => {
    const labels = {
      'chat': '💬 Chat',
      'journal': '📔 Journal',
      'weight': '⚖️ Poids',
      'mealplan': '🍽️ Plans',
      'scanner': '📷 Scanner',
      'challenges': '🏆 Défis',
      'recipes': '📖 Recettes',
      'tips': '💡 Conseils',
      'profile': '👤 Profil',
      'subscription': '💳 Abo'
    };
    return labels[page] || page;
  };

  const getSourceEmoji = (source) => {
    const emojis = {
      'direct': '🔗',
      'google': '🔍',
      'facebook': '📘',
      'instagram': '📸',
      'tiktok': '🎵',
      'twitter': '🐦',
      'youtube': '▶️',
      'linkedin': '💼'
    };
    return emojis[source] || '🌐';
  };

  const formatTime = (date) => new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-title">
          <Activity size={28} />
          <h1>Dashboard</h1>
          <span className="admin-live-badge">● Live</span>
        </div>
        <button className="admin-refresh" onClick={loadStats}>
          <RefreshCw size={18} />
          Actualiser
        </button>
      </div>

      {/* Stats principales */}
      <div className="admin-stats-row">
        <div className="admin-stat live">
          <Activity size={24} />
          <div className="stat-info">
            <span className="stat-number">{stats.live}</span>
            <span className="stat-label">En ligne</span>
          </div>
        </div>
        <div className="admin-stat">
          <Eye size={24} />
          <div className="stat-info">
            <span className="stat-number">{stats.today}</span>
            <span className="stat-label">Vues aujourd'hui</span>
          </div>
        </div>
        <div className="admin-stat">
          <Users size={24} />
          <div className="stat-info">
            <span className="stat-number">{stats.todayUnique}</span>
            <span className="stat-label">Visiteurs aujourd'hui</span>
          </div>
        </div>
        <div className="admin-stat">
          <TrendingUp size={24} />
          <div className="stat-info">
            <span className="stat-number">{stats.unique}</span>
            <span className="stat-label">Total unique</span>
          </div>
        </div>
      </div>

      <div className="admin-grid">
        {/* Graphique 7 jours */}
        <div className="admin-card wide">
          <h3>📊 7 derniers jours</h3>
          <div className="admin-chart">
            {dailyStats.map((day, i) => (
              <div key={i} className="chart-col">
                <div className="chart-bar-wrap">
                  <div 
                    className="chart-bar"
                    style={{ height: `${Math.max(5, (day.views / Math.max(...dailyStats.map(d => d.views), 1)) * 100)}%` }}
                  >
                    <span>{day.views}</span>
                  </div>
                </div>
                <span className="chart-label">{day.date}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pays */}
        <div className="admin-card">
          <h3><Globe size={18} /> Pays</h3>
          <div className="admin-list">
            {countries.length > 0 ? countries.map((c, i) => (
              <div key={i} className="list-item">
                <span className="list-name">{c.name}</span>
                <span className="list-count">{c.count}</span>
              </div>
            )) : <p className="empty-text">Aucune donnée</p>}
          </div>
        </div>

        {/* Sources */}
        <div className="admin-card">
          <h3><Share2 size={18} /> Sources</h3>
          <div className="admin-list">
            {sources.length > 0 ? sources.map((s, i) => (
              <div key={i} className="list-item">
                <span className="list-name">{getSourceEmoji(s.name)} {s.name}</span>
                <span className="list-count">{s.count}</span>
              </div>
            )) : <p className="empty-text">Aucune donnée</p>}
          </div>
        </div>

        {/* Top Pages */}
        <div className="admin-card">
          <h3>🔥 Pages populaires</h3>
          <div className="admin-list">
            {topPages.length > 0 ? topPages.map((p, i) => (
              <div key={i} className="list-item">
                <span className="list-rank">#{i+1}</span>
                <span className="list-name">{getPageLabel(p.page)}</span>
                <span className="list-count">{p.count}</span>
              </div>
            )) : <p className="empty-text">Aucune donnée</p>}
          </div>
        </div>

        {/* Appareils */}
        <div className="admin-card">
          <h3>📱 Appareils</h3>
          <div className="devices-row">
            <div className="device-item">
              <Smartphone size={24} />
              <span className="device-count">{devices.mobile}</span>
              <span className="device-label">Mobile</span>
            </div>
            <div className="device-item">
              <Monitor size={24} />
              <span className="device-count">{devices.desktop}</span>
              <span className="device-label">Desktop</span>
            </div>
            <div className="device-item">
              <Tablet size={24} />
              <span className="device-count">{devices.tablet}</span>
              <span className="device-label">Tablet</span>
            </div>
          </div>
        </div>

        {/* Navigateurs */}
        <div className="admin-card">
          <h3>🌐 Navigateurs</h3>
          <div className="admin-list">
            {browsers.length > 0 ? browsers.map((b, i) => (
              <div key={i} className="list-item">
                <span className="list-name">{b.name}</span>
                <div className="progress-bar">
                  <div style={{ width: `${(b.count / Math.max(...browsers.map(x => x.count), 1)) * 100}%` }}></div>
                </div>
                <span className="list-count">{b.count}</span>
              </div>
            )) : <p className="empty-text">Aucune donnée</p>}
          </div>
        </div>

        {/* Dernières visites */}
        <div className="admin-card wide">
          <h3>🕐 Dernières visites</h3>
          <div className="visits-list">
            {recentVisits.length > 0 ? recentVisits.map((v, i) => (
              <div key={i} className="visit-row">
                <span className="visit-time">{formatTime(v.created_at)}</span>
                <span className="visit-page">{getPageLabel(v.page)}</span>
                <span className="visit-country">{v.country || '🌍'}</span>
                <span className="visit-source">{getSourceEmoji(v.source)} {v.source || 'direct'}</span>
                <span className="visit-device">
                  {v.device_type === 'mobile' ? '📱' : v.device_type === 'tablet' ? '📱' : '💻'}
                </span>
              </div>
            )) : <p className="empty-text">Aucune visite enregistrée</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
